/**
 * Linking a new guest app user to a profile.
 *
 * An account with no `profileId` is not a half-configured account, it is a broken one: the sign-in
 * exchange dereferences the profile, so the person receives their six-digit code and then gets a
 * server error, and the dead account shadows the self-registration that would have worked. So the
 * create screen insists on a profile, either an existing one or a new one it creates first.
 */
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {ProfileType} from "crqlar-react-common/dist/models/profile/profileType.model";

export type ProfileLinkMode = 'existing' | 'new'

export interface NewProfileDraft {
    firstName?: string;
    lastName?: string;
    profileTypeC?: ProfileType;
}

const filled = (value?: string) => !!value?.trim()

/** Whether the new-profile form has enough to create one. */
export const newProfileDraftIsComplete = (draft: NewProfileDraft): boolean => (
    filled(draft.firstName) && filled(draft.lastName) && !!draft.profileTypeC
)

/**
 * Whether the screen may save.
 *
 * The profile requirement applies to creation only. Four of Forsthofgut's 21,468 existing accounts
 * already have no profile, and blocking their form would leave them uneditable rather than fixed.
 */
export const canSaveGuestAppUser = (
    user: {email?: string; profileId?: string},
    isNew: boolean,
    mode: ProfileLinkMode,
    draft: NewProfileDraft,
): boolean => {
    if (!filled(user.email)) return false
    if (!isNew) return true
    return mode === 'new' ? newProfileDraftIsComplete(draft) : !!user.profileId
}

/**
 * The profile to create for a new account.
 *
 * The account's own address becomes the profile's primary email, which is what the sign-in lookup
 * matches on, so the two agree from the start rather than after somebody notices.
 */
export const profileFromDraft = (draft: NewProfileDraft, email?: string): Profile => ({
    firstName: draft.firstName?.trim(),
    lastName: draft.lastName?.trim(),
    profileTypeC: draft.profileTypeC,
    emails: filled(email)
        ? [{email: email!.trim(), isPrimary: true, displaySequence: 1}]
        : [],
} as Profile)
